import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import { IssueReportRecord } from './issueReport.props';

const selectIssueReport = (state: RootState) => state.issueReport;

/** Reports in the order they were filed. */
const selectIssueReports = createSelector(
  selectIssueReport,
  (state): IssueReportRecord[] =>
    state.order.map((id) => state.byId[id]).filter(Boolean)
);

const selectIssueReportById = (issueId: string | null) =>
  createSelector(selectIssueReport, (state) =>
    issueId ? state.byId[issueId] ?? null : null
  );

/**
 * The denominator in HITL-1501's `1 / 5` chip. Voided reports are excluded — a
 * retracted flag is not something the agent still has to account for.
 */
const selectIssueReportTotalCount = createSelector(
  selectIssueReports,
  (reports) => reports.filter((report) => !report.voided).length
);

/**
 * The numerator in `1 / 5`. FR-5 wants unclassified only; the chip is HIDDEN at zero
 * and must never render `0 / 5` — that gate belongs to HITL-1501, this is its input.
 */
const selectUnclassifiedIssueReportCount = createSelector(
  selectIssueReports,
  (reports) =>
    reports.filter((report) => !report.voided && !report.issue_type).length
);

const selectHasFailedIssueReports = createSelector(
  selectIssueReports,
  (reports) => reports.some((report) => report.status === 'failed')
);

/** The record inside the open flag window — what the toast and the tray both act on. */
const selectActiveIssueReport = createSelector(
  selectIssueReport,
  (state): IssueReportRecord | null =>
    state.activeReportId ? state.byId[state.activeReportId] ?? null : null
);

const selectActiveFlagWindowExpiresAt = createSelector(
  selectIssueReport,
  (state) => state.activeWindowExpiresAt
);

const selectStagedIssueType = createSelector(
  selectIssueReport,
  (state) => state.stagedIssueType
);

export {
  selectActiveFlagWindowExpiresAt,
  selectActiveIssueReport,
  selectHasFailedIssueReports,
  selectIssueReport,
  selectIssueReportById,
  selectIssueReportTotalCount,
  selectIssueReports,
  selectStagedIssueType,
  selectUnclassifiedIssueReportCount,
};
